import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
// The base files plus the redesign fragments, merged exactly as the mobile
// app merges them (src/i18n/bundles.ts).
import { ar, en } from '../../../src/i18n/bundles';

export type Language = 'ar' | 'en';

/**
 * The SAME locale files the mobile app ships, so a string added for one client
 * exists in the other and src/i18n/translationKeys.test.ts keeps guarding both.
 *
 * Namat's web app hand-rolls this rather than pulling in i18next, and the JSON
 * is nested identically, so the lookup is a dotted path.
 */
const translations: Record<Language, unknown> = { ar, en };

const STORAGE_KEY = 'fusilat_language';

type LanguageContextValue = {
  language: Language;
  setLanguage: (l: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  dir: 'rtl' | 'ltr';
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // A component rendered outside the provider gets Arabic and the key back,
    // rather than an exception that blanks the whole screen.
    return { language: 'ar', setLanguage: () => {}, t: (k) => k, dir: 'rtl' };
  }
  return ctx;
}

function lookup(lang: Language, key: string): string | null {
  let value: unknown = translations[lang];
  for (const part of key.split('.')) {
    if (value && typeof value === 'object' && part in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return null;
    }
  }
  // A path that stops on a branch is a miss, not "[object Object]" on screen.
  return typeof value === 'string' ? value : null;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'ar';
    } catch {
      // Private windows can refuse storage entirely; Arabic is the default.
      return 'ar';
    }
  });

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // A failed write costs the preference on the next visit, nothing more.
    }
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  }, [language, dir]);

  const t = (key: string, params?: Record<string, string | number>) => {
    /*
     * A counted string picks its form from the number, i18next's key
     * convention (`key_one`, `key_few`, …) so the mobile app and this one
     * read the SAME locale files. Arabic has six categories, not two:
     * "3 وحدة" is wrong the way "3 unit" is in English, and the live walk
     * showed exactly that on a child's syllabus.
     */
    const count = params?.count;
    const plural = typeof count === 'number'
      ? [`${key}_${new Intl.PluralRules(language).select(count)}`, `${key}_other`]
      : [];
    const found = plural.reduce<string | null>(
      (hit, k) => hit ?? lookup(language, k) ?? lookup('ar', k), null,
    // Arabic is the fallback, not English: it is the complete set.
    ) ?? lookup(language, key) ?? lookup('ar', key);
    if (found === null) return key;
    if (!params) return found;
    /*
     * `count` picks the form; `n` is what the reader sees. A caller that
     * gives only a count still reads correctly — grouped with Western
     * digits, which these apps use in every language — rather than showing
     * a raw {{n}} to the user.
     */
    const filled = typeof count === 'number' && !('n' in params)
      ? { ...params, n: new Intl.NumberFormat('en-US').format(count) }
      : params;
    return found.replace(/\{\{(\w+)\}\}/g, (whole, name: string) =>
      name in filled ? String(filled[name]) : whole
    );
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
}
